import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import YouTube from 'react-youtube'

/**
 * YouTubePlayer wraps react-youtube and exposes a play/pause/seek API via ref
 * that mirrors enough of the HTMLAudioElement interface for Player.js to use it.
 *
 * Exposed handle (ref):
 *   play()          - returns Promise (resolves immediately)
 *   pause()
 *   get paused      - boolean
 *   get duration    - number (seconds)
 *   get currentTime - number (seconds)
 *   set currentTime - seeks
 *   set volume(0-1) - sets volume (0–100 mapped internally)
 *   set autoplay    - ignored (handled via opts)
 *   removeAttribute('src') / load() - treated as stop/reset (no-op safe)
 */
const YouTubePlayer = forwardRef(function YouTubePlayer(
    { youtubeId, shouldPlay, volume, onReady, onPlay, onPause, onEnd, onError, onTimeUpdate, onIframeToggle },
    ref
) {
    const playerRef = useRef(null)   // YT.Player instance
    const pausedRef = useRef(true)
    const intervalRef = useRef(null)
    // Guards against spurious ENDED events fired by the YouTube IFrame API when
    // cueVideoById() is called while the player is already in the ENDED state.
    // The API transitions through ENDED again as part of the state machine reset,
    // which would otherwise cause the new track to be immediately reported as finished.
    const hasPlayedRef = useRef(false)

    useImperativeHandle(ref, () => ({
        play() {
            playerRef.current?.playVideo()
            return Promise.resolve()
        },
        pause() {
            playerRef.current?.pauseVideo()
        },
        get paused() {
            return pausedRef.current
        },
        get duration() {
            return playerRef.current?.getDuration() ?? 0
        },
        get currentTime() {
            return playerRef.current?.getCurrentTime() ?? 0
        },
        set currentTime(t) {
            playerRef.current?.seekTo(t, true)
        },
        set volume(v) {
            // v is 0–1 from Player.js (audio.volume = volume / 100)
            playerRef.current?.setVolume(Math.round(v * 100))
        },
        // no-ops to keep Player.js happy
        set autoplay(_) {},
        set preload(_) {},
        set src(_) {},
        removeAttribute() {},
        load() {},
        getIframe() {
            return playerRef.current?.getIframe() ?? null
        },
    }), [])

    // Start/stop the polling interval for time updates
    function startPolling() {
        stopPolling()
        intervalRef.current = setInterval(() => {
            onTimeUpdate?.()
        }, 250)
    }

    function stopPolling() {
        clearInterval(intervalRef.current)
        intervalRef.current = null
    }

    useEffect(() => {
        return () => stopPolling()
    }, [])

    // React to shouldPlay / youtubeId changes from Player.js
    useEffect(() => {
        if (!playerRef.current) return
        if (shouldPlay) {
            playerRef.current.playVideo()
        } else {
            playerRef.current.pauseVideo()
        }
    }, [shouldPlay])

    // When the youtubeId changes, cue the new video
    useEffect(() => {
        if (!playerRef.current || !youtubeId) return
        hasPlayedRef.current = false   // new video hasn't played yet
        playerRef.current.cueVideoById(youtubeId)
    }, [youtubeId])

    function handleReady(event) {
        playerRef.current = event.target
        hasPlayedRef.current = false   // fresh player instance, hasn't played yet
        // Apply current volume immediately
        playerRef.current.setVolume(Math.round((volume ?? 1) * 100))
        onReady?.()
        if (shouldPlay) {
            playerRef.current.playVideo()
        }
    }

    function handleStateChange(event) {
        const YT = window.YT?.PlayerState
        if (!YT) return
        switch (event.data) {
            case YT.PLAYING:
                hasPlayedRef.current = true
                pausedRef.current = false
                startPolling()
                onPlay?.()
                // If Player.js thought we were paused, the user clicked the iframe to resume
                if (!shouldPlay) onIframeToggle?.(false)
                break
            case YT.PAUSED:
                pausedRef.current = true
                stopPolling()
                onPause?.()
                // If Player.js thought we were playing, the user clicked the iframe to pause.
                // Guard with hasPlayedRef: a PAUSED event before the video has started
                // (e.g. during buffering/cuing) is not a user-initiated action.
                if (shouldPlay && hasPlayedRef.current) onIframeToggle?.(true)
                break
            case YT.ENDED:
                // Guard with hasPlayedRef: the YouTube IFrame API fires a spurious ENDED
                // state change when cueVideoById() is called while the player is already
                // in the ENDED state (i.e. the previous track just finished). Without this
                // guard that spurious event would cause the newly-cued track to be
                // immediately reported as finished.
                if (hasPlayedRef.current) {
                    hasPlayedRef.current = false
                    pausedRef.current = true
                    stopPolling()
                    onEnd?.()
                }
                break
            case YT.BUFFERING:
                // still "playing" from the user's perspective
                break
            default:
                break
        }
    }

    function handleError(event) {
        console.error('YouTubePlayer error:', event.data)
        onError?.(event)
    }

    const opts = {
        width: '200',
        height: '200',
        playerVars: {
            autoplay: shouldPlay ? 1 : 0,
            controls: 0,
            disablekb: 1,
            fs: 1,
            iv_load_policy: 3,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
        },
    }

    // 200×200 is the minimum size required by YouTube's Terms of Service.
    // The Player bar expands to accommodate this height.
    return (
        <div style={{
            width: 200,
            height: 200,
            borderRadius: 4,
            flexShrink: 0,
            overflow: 'hidden',
        }}>
            <YouTube
                videoId={youtubeId}
                opts={opts}
                onReady={handleReady}
                onStateChange={handleStateChange}
                onError={handleError}
            />
        </div>
    )
})

export default YouTubePlayer

